import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
    UserPlus,
    Crown,
    MagnifyingGlass,
    Copy,
    XCircle,
    CheckCircle,
    Star,
    Lightning,
    Users as UsersIcon,
    Trash,
} from "@phosphor-icons/react";

/**
 * Admin Users Panel — list, comp, grant, revoke.
 *
 * Two ways to give someone free Lifetime access:
 *   1. CREATE COMP — brand-new email, generates random password (for influencers)
 *   2. GRANT LIFETIME — existing user (e.g. your own real test account)
 */
export default function AdminUsersPanel() {
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState(null);
    const [tier, setTier] = useState("");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [showCompModal, setShowCompModal] = useState(false);
    const [showGrantModal, setShowGrantModal] = useState(false);
    const [credentialsModal, setCredentialsModal] = useState(null); // {email, temporary_password, instructions}

    const loadStats = useCallback(async () => {
        try {
            const r = await api.get("/admin/stats");
            setStats(r.data);
        } catch (err) {
            console.error("Stats load failed:", err);
        }
    }, []);

    const loadUsers = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (tier) params.set("tier", tier);
            if (search) params.set("search", search);
            const r = await api.get(`/admin/users?${params.toString()}`);
            setUsers(r.data.users);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load users");
        } finally {
            setLoading(false);
        }
    }, [tier, search]);

    useEffect(() => {
        loadStats();
        loadUsers();
    }, [loadStats, loadUsers]);

    const onRevoke = async (email) => {
        if (!window.confirm(`Revoke comp Lifetime for ${email}? This only works on comp accounts (not paying customers).`)) return;
        try {
            await api.post("/admin/revoke-lifetime", { email });
            toast.success(`Revoked Lifetime for ${email}`);
            loadStats();
            loadUsers();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Revoke failed");
        }
    };

    return (
        <div>
            {/* Stats strip */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                    <StatCard label="TOTAL USERS" value={stats.users.total} icon={UsersIcon} />
                    <StatCard label="LIFETIME" value={stats.users.lifetime} icon={Crown} accent />
                    <StatCard label="FOUNDER ACTIVE" value={stats.users.founder_active} icon={Star} />
                    <StatCard label="COMP ACCOUNTS" value={stats.users.comp_accounts} icon={Lightning} />
                    <StatCard label="SEATS LEFT" value={stats.cohort.seats_remaining} icon={CheckCircle} />
                </div>
            )}

            {/* Action bar */}
            <div className="flex flex-col md:flex-row gap-3 mb-5">
                <button
                    data-testid="admin-create-comp-btn"
                    onClick={() => setShowCompModal(true)}
                    className="bg-electric-blue hover:bg-electric-blue-dark text-white font-bold px-5 py-2.5 text-xs tracking-wider uppercase transition-all flex items-center gap-2"
                >
                    <UserPlus size={14} weight="bold" /> CREATE COMP LIFETIME
                </button>
                <button
                    data-testid="admin-grant-lifetime-btn"
                    onClick={() => setShowGrantModal(true)}
                    className="border border-electric-blue text-electric-blue hover:bg-electric-blue hover:text-white px-5 py-2.5 text-xs tracking-wider uppercase font-bold transition-all flex items-center gap-2"
                >
                    <Crown size={14} weight="bold" /> GRANT LIFETIME TO EXISTING USER
                </button>

                <div className="flex-1" />

                {/* Filters */}
                <div className="flex items-center gap-2">
                    <select
                        data-testid="admin-tier-filter"
                        value={tier}
                        onChange={(e) => setTier(e.target.value)}
                        className="bg-[#0A0A0A] border border-white/10 text-xs label-mono px-3 py-2"
                    >
                        <option value="">ALL TIERS</option>
                        <option value="lifetime">LIFETIME</option>
                        <option value="founder">FOUNDER</option>
                        <option value="free">FREE / NOT PAID</option>
                    </select>
                    <div className="flex items-center gap-2 border border-white/10 px-3 py-2 bg-[#0A0A0A]">
                        <MagnifyingGlass size={12} className="text-muted-foreground" />
                        <input
                            data-testid="admin-search-input"
                            type="text"
                            placeholder="email contains..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="bg-transparent text-xs outline-none w-48"
                        />
                    </div>
                </div>
            </div>

            {/* Users table */}
            <div className="bg-[#0A0A0A] border border-white/10 overflow-x-auto">
                <table className="w-full">
                    <thead className="border-b border-white/10">
                        <tr>
                            <th className="text-left px-5 py-3 label-mono text-[10px]">EMAIL</th>
                            <th className="text-left px-5 py-3 label-mono text-[10px]">DISPLAY NAME</th>
                            <th className="text-left px-5 py-3 label-mono text-[10px]">TIER</th>
                            <th className="text-left px-5 py-3 label-mono text-[10px]">COMP</th>
                            <th className="text-left px-5 py-3 label-mono text-[10px]">CREATED</th>
                            <th className="text-right px-5 py-3 label-mono text-[10px]">ACTIONS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={6} className="p-6 label-mono text-muted-foreground">LOADING...</td></tr>
                        )}
                        {!loading && users.length === 0 && (
                            <tr><td colSpan={6} className="p-6 label-mono text-muted-foreground">NO USERS MATCH FILTERS</td></tr>
                        )}
                        {users.map((u, i) => (
                            <tr
                                key={u.id}
                                data-testid={`admin-user-row-${i}`}
                                className="border-b border-white/5 hover:bg-electric-blue/5 transition-colors"
                            >
                                <td className="px-5 py-3 text-sm">{u.email}</td>
                                <td className="px-5 py-3 text-sm">{u.display_name}</td>
                                <td className="px-5 py-3">
                                    <TierBadge tier={u.tier} />
                                </td>
                                <td className="px-5 py-3">
                                    {u.comp_account ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-500/10 text-yellow-400 border border-yellow-500/30 text-[10px] label-mono">
                                            <Star size={10} weight="fill" /> COMP
                                        </span>
                                    ) : (
                                        <span className="text-muted-foreground text-xs">—</span>
                                    )}
                                </td>
                                <td className="px-5 py-3 text-xs text-muted-foreground">
                                    {new Date(u.created_at).toLocaleDateString()}
                                </td>
                                <td className="px-5 py-3 text-right">
                                    {u.comp_account && u.lifetime_member && (
                                        <button
                                            data-testid={`admin-revoke-btn-${i}`}
                                            onClick={() => onRevoke(u.email)}
                                            className="text-red-400 hover:text-red-300 text-xs label-mono inline-flex items-center gap-1"
                                            title="Revoke comp Lifetime"
                                        >
                                            <Trash size={12} weight="bold" /> REVOKE
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Modals */}
            {showCompModal && (
                <CreateCompModal
                    onClose={() => setShowCompModal(false)}
                    onCreated={(creds) => {
                        setShowCompModal(false);
                        setCredentialsModal(creds);
                        loadStats();
                        loadUsers();
                    }}
                />
            )}
            {showGrantModal && (
                <GrantLifetimeModal
                    onClose={() => setShowGrantModal(false)}
                    onGranted={() => {
                        setShowGrantModal(false);
                        loadStats();
                        loadUsers();
                    }}
                />
            )}
            {credentialsModal && (
                <CredentialsModal
                    creds={credentialsModal}
                    onClose={() => setCredentialsModal(null)}
                />
            )}
        </div>
    );
}

function StatCard({ label, value, icon: Icon, accent }) {
    return (
        <div className={`bg-[#0A0A0A] border ${accent ? "border-electric-blue/40" : "border-white/10"} p-4`}>
            <div className="flex items-center justify-between mb-1">
                <p className="label-mono text-[9px] text-muted-foreground">{label}</p>
                <Icon size={12} weight="fill" className={accent ? "text-electric-blue" : "text-muted-foreground"} />
            </div>
            <p className={`font-heading text-2xl font-black ${accent ? "text-electric-blue" : "text-white"}`}>{value}</p>
        </div>
    );
}

function TierBadge({ tier }) {
    if (tier === "lifetime") {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-electric-blue/10 text-electric-blue border border-electric-blue/40 text-[10px] label-mono">
                <Crown size={10} weight="fill" /> LIFETIME
            </span>
        );
    }
    if (tier === "founder") {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 text-[10px] label-mono">
                <Star size={10} weight="fill" /> FOUNDER
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/5 text-muted-foreground border border-white/10 text-[10px] label-mono">
            FREE
        </span>
    );
}

function CreateCompModal({ onClose, onCreated }) {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [note, setNote] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const r = await api.post("/admin/comp-account", {
                email: email.trim(),
                display_name: name.trim() || email.split("@")[0],
                note: note.trim() || null,
            });
            toast.success(`Comp Lifetime created for ${email}`);
            onCreated(r.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to create comp account");
        } finally {
            setLoading(false);
        }
    };

    return (
        <ModalShell onClose={onClose} title="Create Comp Lifetime Account" testId="comp-modal">
            <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
                Brand-new free Lifetime account. Use for influencers, partners, or comp deals.
                A random one-time password is generated — copy it from the next screen and send
                it to them via your secure channel.
            </p>
            <form onSubmit={submit} className="space-y-4">
                <Field label="EMAIL" required>
                    <input
                        data-testid="comp-email-input"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="influencer@example.com"
                        className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none"
                    />
                </Field>
                <Field label="DISPLAY NAME" hint="What they'll see in the dashboard">
                    <input
                        data-testid="comp-name-input"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Auto-derived from email if blank"
                        className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none"
                    />
                </Field>
                <Field label="INTERNAL NOTE" hint="For your records only — never shown to user">
                    <textarea
                        data-testid="comp-note-input"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={2}
                        placeholder="@cooltiktoker · 2.4M followers · partnership Q2 2026"
                        className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none resize-none"
                    />
                </Field>
                <div className="flex gap-2 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 border border-white/10 text-muted-foreground hover:text-white px-4 py-2.5 text-xs label-mono transition-colors"
                    >
                        CANCEL
                    </button>
                    <button
                        data-testid="comp-submit-btn"
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-electric-blue hover:bg-electric-blue-dark text-white font-bold px-4 py-2.5 text-xs label-mono disabled:opacity-50 transition-all"
                    >
                        {loading ? "CREATING..." : "CREATE COMP ACCOUNT"}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
}

function GrantLifetimeModal({ onClose, onGranted }) {
    const [email, setEmail] = useState("");
    const [note, setNote] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/admin/grant-lifetime", {
                email: email.trim(),
                note: note.trim() || null,
            });
            toast.success(`${email} is now Lifetime`);
            onGranted();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to grant Lifetime");
        } finally {
            setLoading(false);
        }
    };

    return (
        <ModalShell onClose={onClose} title="Grant Lifetime to Existing User" testId="grant-modal">
            <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
                Upgrade a user that ALREADY has an account. Use for: your own real test
                account (after refunding the test charge), or any existing member you want
                to comp.
            </p>
            <form onSubmit={submit} className="space-y-4">
                <Field label="EMAIL" required>
                    <input
                        data-testid="grant-email-input"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="user@example.com"
                        className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none"
                    />
                </Field>
                <Field label="INTERNAL NOTE">
                    <textarea
                        data-testid="grant-note-input"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={2}
                        placeholder="Owner test account · permanent admin"
                        className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none resize-none"
                    />
                </Field>
                <div className="flex gap-2 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 border border-white/10 text-muted-foreground hover:text-white px-4 py-2.5 text-xs label-mono transition-colors"
                    >
                        CANCEL
                    </button>
                    <button
                        data-testid="grant-submit-btn"
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-electric-blue hover:bg-electric-blue-dark text-white font-bold px-4 py-2.5 text-xs label-mono disabled:opacity-50 transition-all"
                    >
                        {loading ? "GRANTING..." : "GRANT LIFETIME"}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
}

function CredentialsModal({ creds, onClose }) {
    const [copied, setCopied] = useState(false);
    const copyAll = () => {
        const text = `Email: ${creds.user.email}\nPassword: ${creds.temporary_password}\nLogin: ${window.location.origin}/login`;
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success("Credentials copied to clipboard");
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <ModalShell onClose={onClose} title="🎉 Comp Account Created" testId="creds-modal" wide>
            <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 mb-5">
                <p className="text-xs text-yellow-300 leading-relaxed">
                    <strong>This password is shown ONCE.</strong> Copy it now and send it to them via your secure channel.
                    If you lose it, you'll need to delete this account and create a new one.
                </p>
            </div>

            <div className="space-y-3 mb-5">
                <CredsRow label="Email" value={creds.user.email} />
                <CredsRow label="Temporary Password" value={creds.temporary_password} mono accent />
                <CredsRow label="Login URL" value={`${window.location.origin}/login`} />
            </div>

            <div className="flex gap-2">
                <button
                    onClick={copyAll}
                    data-testid="creds-copy-btn"
                    className="flex-1 bg-electric-blue hover:bg-electric-blue-dark text-white font-bold px-4 py-3 text-xs label-mono flex items-center justify-center gap-2 transition-all"
                >
                    {copied ? <CheckCircle size={14} weight="fill" /> : <Copy size={14} weight="bold" />}
                    {copied ? "COPIED" : "COPY ALL CREDENTIALS"}
                </button>
                <button
                    onClick={onClose}
                    className="px-4 py-3 border border-white/10 text-muted-foreground hover:text-white text-xs label-mono transition-colors"
                >
                    DONE
                </button>
            </div>
        </ModalShell>
    );
}

function CredsRow({ label, value, mono, accent }) {
    return (
        <div>
            <p className="label-mono text-[10px] text-muted-foreground mb-1">{label}</p>
            <p className={`${mono ? "font-mono" : "font-medium"} ${accent ? "text-electric-blue" : "text-white"} text-base bg-black/40 border border-white/10 px-3 py-2 break-all`}>
                {value}
            </p>
        </div>
    );
}

function Field({ label, hint, required, children }) {
    return (
        <div>
            <label className="label-mono text-[10px] text-muted-foreground block mb-1.5">
                {label}{required ? " *" : ""}
                {hint && <span className="ml-2 text-[9px] text-white/40 normal-case tracking-normal">— {hint}</span>}
            </label>
            {children}
        </div>
    );
}

function ModalShell({ children, onClose, title, testId, wide }) {
    return (
        <div
            data-testid={testId}
            className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-black/85 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className={`bg-[#0A0A0A] border border-electric-blue/40 ${wide ? "max-w-xl" : "max-w-md"} w-full neon-border relative`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-electric-blue to-transparent" />
                <div className="flex items-center justify-between p-5 border-b border-white/10">
                    <h3 className="font-heading text-lg font-black tracking-tight">{title}</h3>
                    <button onClick={onClose} className="text-muted-foreground hover:text-white">
                        <XCircle size={20} weight="bold" />
                    </button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}
